// MicrograntAwardeeForm.jsx
import React, { useState, useEffect } from "react";
import { collection, addDoc, getDocs, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import Confetti from "react-confetti";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageTaskbar from "../../components/ui/Taskbar/PageTaskbar";
import OSIcon32 from "../../components/icons/OSIcon32";
import { FALLBACK_CHAPTERS, FALLBACK_CHAPTER_SLUG_TO_NAME } from "../../data/chapterFallbacks";

// Chrome window shell — the standalone page reads as a maximized OS window.
const chromeWindowStyle = {
  background: "var(--chrome-face)",
  border: "2px solid",
  borderColor: "var(--bevel-raised)",
  boxShadow: "var(--shadow-raised), var(--window-drop)",
  display: "flex",
  flexDirection: "column",
  margin: "0 auto",
  width: "95%",
  maxWidth: 720,
};

// Raised chrome intro panel — replaces the retired ink-border card.
const introPanelStyle = {
  padding: "clamp(14px, 3.5vw, 22px) clamp(14px, 3.5vw, 24px)",
  fontSize: "14px",
  lineHeight: "1.55",
  background: "var(--chrome-light)",
  border: "2px solid",
  borderColor: "var(--bevel-raised)",
  boxShadow: "var(--shadow-raised)",
  color: "var(--mb-ink)",
};

// Form sections live in chrome group boxes instead of ink-rule headers.
const groupboxStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: "14px 16px 18px",
};

export default function MicrograntAwardeeForm() {
  const [searchParams] = useSearchParams();
  const chapterFromQuery = FALLBACK_CHAPTER_SLUG_TO_NAME[searchParams.get("chapter")];
  const emailFromQuery = searchParams.get("email") || "";
  const defaultChapter = chapterFromQuery || "";

  const [form, setForm] = useState({
    chapter: defaultChapter,
    email: emailFromQuery,
    fullName: "",
    addressStreet: "",
    addressCity: "",
    addressState: "",
    addressZip: "",
    socialHandles: "",
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });
  const [chapterOptions, setChapterOptions] = useState(FALLBACK_CHAPTERS);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "chapters"));
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(c => c.active !== false)
          .sort((a, b) => {
            const ao = typeof a.order === "number" ? a.order : 999;
            const bo = typeof b.order === "number" ? b.order : 999;
            if (ao !== bo) return ao - bo;
            return (a.name || "").localeCompare(b.name || "");
          })
          .map(c => ({ slug: c.pageSlug || c.id, name: c.name }));
        if (cancelled || list.length === 0) return;
        setChapterOptions(list);
        if (!defaultChapter) {
          const querySlug = searchParams.get("chapter");
          const matched = list.find(c => c.slug === querySlug);
          if (matched) setForm(prev => ({ ...prev, chapter: matched.name }));
        }
      } catch (e) {
        console.error("Microgrant awardee: failed to load /chapters, using fallback", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleClose = () => {
    try { sessionStorage.setItem("gnf-has-booted", "1"); } catch {}
    try { navigate("/"); } catch (err) {
      console.error("Navigation error:", err);
      window.location.href = "/";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file.");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("Image must be under 10MB.");
      e.target.value = "";
      return;
    }
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (evt) => setPhotoPreview(evt.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.chapter
        || !form.addressStreet || !form.addressCity || !form.addressState || !form.addressZip) {
      alert("Please fill in all required fields.");
      return;
    }
    if (!photo) {
      alert("Please upload a photo for the social media announcement.");
      return;
    }

    setIsSubmitting(true);
    try {
      const ext = (photo.name.split(".").pop() || "jpg")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "") || "jpg";
      const storagePath = `form-uploads/microgrant-awardee/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, photo, {
        contentType: photo.type,
        cacheControl: "public, max-age=86400",
      });
      const photoUrl = await getDownloadURL(storageRef);

      const submission = {
        formType: "microgrant-awardee",
        chapter: form.chapter,
        fullName: form.fullName,
        email: form.email.trim().toLowerCase(),
        addressStreet: form.addressStreet,
        addressCity: form.addressCity,
        addressState: form.addressState,
        addressZip: form.addressZip,
        socialHandles: form.socialHandles || "",
        photoUrl,
        photoStoragePath: storagePath,
        submittedAt: Timestamp.now(),
      };

      await addDoc(collection(db, "formLibrarySubmissions"), submission);
      setSubmitted(true);
    } catch (err) {
      console.error("Microgrant awardee submission error:", err);
      alert("There was an error submitting your form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mb-page" style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        textAlign: "center",
        padding: "20px 20px calc(80px + env(safe-area-inset-bottom))",
        boxSizing: "border-box",
        background: "transparent",
      }}>
        <PageTaskbar windowLabel="Awardee form" windowIcon="note" />
        <div style={{ ...chromeWindowStyle, width: "100%", maxWidth: 560, textAlign: "center" }}>
          <div className="win95-titlebar win95-titlebar--aqua" style={{ cursor: "default" }}>
            <OSIcon32 kind="trophy" size={15} className="win95-titlebar__icon" />
            <span className="win95-titlebar__label" style={{ textAlign: "left" }}>Awardee form</span>
          </div>
          <div style={{ padding: "40px 36px 34px" }}>
            <Confetti width={windowSize.width} height={windowSize.height} numberOfPieces={250} recycle={false} />
            <span className="mb-eyebrow" style={{ color: "var(--mb-aqua-deep)" }}>Information received</span>
            <h1 style={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              textTransform: "none",
              fontSize: 28,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              color: "var(--mb-ink)",
              margin: "12px 0 16px",
            }}>
              Congratulations, again.
            </h1>
            <p style={{ fontSize: 16, lineHeight: 1.5, color: "var(--mb-ink)" }}>
              Your check is on its way and we'll be in touch about the announcement post.
            </p>
            <button
              onClick={handleClose}
              type="button"
              className="mb-btn"
              style={{ marginTop: "24px" }}
            >
              Return to home
              <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formContent = (
    <form onSubmit={handleSubmit} className="mb-form-shell" style={{
      padding: "clamp(16px, 4vw, 28px) clamp(14px, 4vw, 28px) clamp(20px, 4.5vw, 32px)",
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      overflowY: "auto",
      minHeight: 0,
      boxSizing: "border-box",
    }}>
      <div style={introPanelStyle}>
        <span className="mb-eyebrow" style={{ color: "var(--mb-aqua-deep)" }}>Microgrant · awardee information</span>
        <h2 style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          textTransform: "none",
          fontSize: 28,
          letterSpacing: "-0.015em",
          lineHeight: 1.1,
          margin: "10px 0 14px",
          color: "var(--mb-ink)",
        }}>
          Congratulations on your grant.
        </h2>
        <p className="mb-body" style={{ margin: "0 0 12px" }}>
          A few details so we can mail your check, write your check correctly, and put together
          your social media announcement.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
          <span className="mb-badge">Est. time · 4 min</span>
        </div>
      </div>

      <fieldset className="win95-groupbox" style={groupboxStyle}>
        <legend>01 · Check details</legend>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label>Full name *</label>
        <small style={{ marginBottom: 6 }}>
          This is exactly what we'll print on your check, so spell it the way you'd like it to appear.
        </small>
        <input
          type="text"
          name="fullName"
          value={form.fullName}
          onChange={handleChange}
          required
          placeholder="e.g., Jane Maria Smith"
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label>Email address *</label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          required
          placeholder="e.g., jane.smith@gmail.com"
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label>Chapter *</label>
        <select name="chapter" value={form.chapter || ""} onChange={handleChange} required>
          <option value="">Select chapter *</option>
          {chapterOptions.map(c => (
            <option key={c.slug} value={c.name}>{c.name}</option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label>Mailing address — street *</label>
        <small style={{ marginBottom: 6 }}>This is where your check will be mailed.</small>
        <input
          type="text"
          name="addressStreet"
          value={form.addressStreet}
          onChange={handleChange}
          required
          placeholder="e.g., 123 Main St, Apt 4B"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 110px), 1fr))", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label>City *</label>
          <input
            type="text"
            name="addressCity"
            value={form.addressCity}
            onChange={handleChange}
            required
            placeholder="e.g., Buffalo"
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label>State *</label>
          <input
            type="text"
            name="addressState"
            value={form.addressState}
            onChange={handleChange}
            required
            placeholder="e.g., NY"
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label>ZIP *</label>
          <input
            type="text"
            name="addressZip"
            value={form.addressZip}
            onChange={handleChange}
            required
            placeholder="e.g., 14202"
          />
        </div>
      </div>
      </fieldset>

      <fieldset className="win95-groupbox" style={groupboxStyle}>
        <legend>02 · Announcement</legend>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label>Social media handles or websites</label>
        <small style={{ marginBottom: 6 }}>
          Anything you'd like us to tag in our announcement post — Instagram, LinkedIn, your business site.
          List as many as you like, one per line.
        </small>
        <textarea
          name="socialHandles"
          value={form.socialHandles}
          onChange={handleChange}
          rows={3}
          placeholder="e.g., @janesmithdesigns&#10;linkedin.com/in/janesmith&#10;www.janesmithdesigns.com"
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label>Announcement photo *</label>
        <small style={{ marginBottom: 8 }}>
          Please upload a preferred photo for our social media announcement. Ideally a well-lit,
          high-resolution photo of you (or your team).
        </small>
        <input
          type="file"
          accept="image/*"
          onChange={handlePhotoChange}
          required
          style={{ fontFamily: "inherit", fontSize: 13 }}
        />
        {photoPreview && (
          <div style={{ marginTop: 12 }}>
            <img
              src={photoPreview}
              alt="Photo preview"
              style={{
                maxWidth: 240,
                maxHeight: 240,
                objectFit: "cover",
                border: "2px solid",
                borderColor: "var(--bevel-sunken)",
                boxShadow: "none",
              }}
            />
          </div>
        )}
      </div>
      </fieldset>

      <button
        type="submit"
        disabled={isSubmitting}
        className="mb-btn"
        style={{ marginTop: "20px", padding: "16px 24px", fontSize: 14 }}
      >
        {isSubmitting ? "Submitting…" : "Submit information"}
        {!isSubmitting && <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>}
      </button>
    </form>
  );

  return (
    <div className="mb-page" style={{
      minHeight: "100vh",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      display: "flex",
      flexDirection: "column",
      padding: "10px",
      paddingBottom: "calc(80px + env(safe-area-inset-bottom))",
      boxSizing: "border-box",
      background: "transparent",
    }}>
      <PageTaskbar windowLabel="Awardee form" windowIcon="note" />
      <div className="mb-form-shell" style={chromeWindowStyle}>
        <div className="win95-titlebar win95-titlebar--aqua" style={{ cursor: "default", flexShrink: 0 }}>
          <OSIcon32 kind="trophy" size={15} className="win95-titlebar__icon" />
          <span className="win95-titlebar__label">Awardee form</span>
          <div className="win95-titlebar__btns">
            <button
              type="button"
              className="win95-btn-sys win95-close-btn"
              onClick={handleClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
        {formContent}
      </div>
    </div>
  );
}
